import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;
  userPaused = false;

  #abortController = new AbortController();
  #viewportObserver = null;
  #slideMutationObserver = null;
  #inViewport = false;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    if (this.hasAttribute('autoplay')) {
      this.#setupAutoplay();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#viewportObserver?.disconnect();
    this.#slideMutationObserver?.disconnect();
  }

  /**
   * Automatically prepares and plays video when in viewport and slide is active
   */
  #setupAutoplay() {
    const existingVideo = this.querySelector('video');
    const existingIframe = this.querySelector('iframe');
    if (existingVideo) {
      existingVideo.muted = true;
      existingVideo.playsInline = true;
      existingVideo.setAttribute('playsinline', '');
      existingVideo.setAttribute('webkit-playsinline', '');
      existingVideo.setAttribute('loop', '');
      this.setAttribute('data-media-loaded', 'true');
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    } else if (existingIframe) {
      this.setAttribute('data-media-loaded', 'true');
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    } else {
      this.loadContent(false);
    }

    const slide = this.closest('slideshow-slide');

    const handleVisibility = () => {
      const isSlideActive = !slide || slide.getAttribute('aria-hidden') !== 'true';
      if (this.#inViewport && isSlideActive) {
        if (!this.userPaused) {
          this.playMedia();
        }
      } else {
        this.pauseMedia();
        this.userPaused = false;
      }
    };

    if ('IntersectionObserver' in window) {
      this.#viewportObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.#inViewport = entry.isIntersecting;
            handleVisibility();
          });
        },
        { threshold: [0, 0.2] }
      );
      this.#viewportObserver.observe(this);
    } else {
      this.#inViewport = true;
      handleVisibility();
    }

    if (slide && 'MutationObserver' in window) {
      this.#slideMutationObserver = new MutationObserver(() => {
        handleVisibility();
      });
      this.#slideMutationObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    const slideshow = this.closest('slideshow-component') || this.closest('media-gallery');
    if (slideshow) {
      const signal = this.#abortController.signal;
      slideshow.addEventListener(ThemeEvents.slideshowSelect || 'slideshow:select', handleVisibility, { signal });
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.userPaused = false;
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      content.muted = true;
      content.playsInline = true;
      content.setAttribute('playsinline', '');
      content.setAttribute('webkit-playsinline', '');
      if (content.getAttribute('autoplay') || this.hasAttribute('autoplay')) {
        content.play().catch(() => {});
      }
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.userPaused = true;
      this.pauseMedia();
    } else {
      this.userPaused = false;
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]') || this.querySelector('iframe');
    if (iframe) {
      const isVimeo =
        iframe.dataset.videoType === 'vimeo' ||
        iframe.classList.contains('js-vimeo') ||
        (iframe.src && iframe.src.includes('vimeo'));
      iframe.contentWindow?.postMessage(
        isVimeo ? '{"method":"play"}' : '{"event":"command","func":"playVideo","args":""}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.play().catch(() => {});
      }
    }
    this.isPlaying = true;
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]') || this.querySelector('iframe');

    if (iframe) {
      const isVimeo =
        iframe.dataset.videoType === 'vimeo' ||
        iframe.classList.contains('js-vimeo') ||
        (iframe.src && iframe.src.includes('vimeo'));
      iframe.contentWindow?.postMessage(
        isVimeo ? '{"method":"pause"}' : '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
